import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { EMPTY_PERMISSIONS, FULL_PERMISSIONS } from '../types';
import type { Permissions, PermissionKey } from '../types';

interface UsePermissionsResult extends Permissions {
  permissions: Permissions;
  isSubuser: boolean;
  isAdmin: boolean;
  parentUsername: string | null;
  parentUserId: number | null;
  has: (key: PermissionKey) => boolean;
  hasAny: (...keys: PermissionKey[]) => boolean;
  hasAll: (...keys: PermissionKey[]) => boolean;
}

export function usePermissions(): UsePermissionsResult {
  const { user, isAuthenticated } = useAuth();

  return useMemo(() => {
    const raw = user?.permissions ?? (isAuthenticated ? FULL_PERMISSIONS : EMPTY_PERMISSIONS);
    const permissions: Permissions = raw as Permissions;
    const isAdmin = user?.is_admin ?? false;

    return {
      ...permissions,
      permissions,
      isSubuser: isAuthenticated ? !isAdmin : false,
      isAdmin,
      parentUsername: user?.parent_username ?? null,
      parentUserId: user?.parent_user_id ?? null,
      has: (key) => permissions[key] === true,
      hasAny: (...keys) => keys.some((k) => permissions[k] === true),
      hasAll: (...keys) => keys.every((k) => permissions[k] === true),
    };
  }, [user, isAuthenticated]);
}
